import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { useDownloadBlob, useNativeDebounce, useRequest } from '@main/hooks';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import { CircularProgress, Grid, Switch } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
  PopList,
} from '@main/components';
import { showMessage } from 'app/store/core/messageSlice';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import Button from '@mui/material/Button';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import { formatDate } from 'src/app/utils/dates';
import {
  exportFile,
  searchLocations,
  statusLocation,
  catalogCompanies,
} from 'src/app/api/locations';
import { useExtractSelector } from '@main/hooks/useExtractSelector';
import { typeDownloadEnum } from 'src/app/enums/fileEnum';
import _ from 'lodash';
import { customSwitchStyles } from 'app/styles/styled/customSwitchStyles';
import { defaultsEnum } from 'src/app/enums/defaultsEnum';
import { useTheme } from '@mui/styles';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const exportFileOptions = _.omit(typeDownloadEnum, ['txt', 'zip']);

const statusRegistryData = [
  {
    label: 'Todo',
    value: '',
  },
  {
    label: 'Activo',
    value: 'true',
  },
  {
    label: 'Inactivo',
    value: 'false',
  },
];

const tableRowScheme = [
  {
    title: 'Clave',
    maxWidth: '280px',
    minWidth: '240px',
  },
  {
    title: 'Nombre',
    minWidth: '180px',
  },
  {
    title: 'Clave Compañía',
    minWidth: '180px',
  },
  {
    title: 'No. de usuarios',
    minWidth: '120px',
  },
  {
    title: 'Terminales de Registro',
    minWidth: '170px',
  },
  {
    title: 'Torniquetes',
    minWidth: '120px',
  },
  {
    title: 'Reconocimiento',
    minWidth: '120px',
  },
  {
    title: 'Kioskos',
    minWidth: '120px',
  },
  {
    title: 'Estatus',
    minWidth: '100px',
  },
  {
    title: 'Fecha',
    minWidth: '180px',
  },
];

const Locations = () => {
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const openAlert = useSimpleAlert();
  // ------ States correspondiente a componentes de filtrado -------
  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  const [form, setForm] = useState({
    query: '',
    initDate: '',
    endDate: '',
    paginate: undefined,
  });
  const theme = useTheme();
  // ----- Instancia de request que se usaran en la vista junto a sus valores de estado ---------
  // ------- Selects --------------
  const [statusSelect, setStatusSelect] = useState('');
  const [companySelect, setcompanySelect] = useState('');
  // ------ State general de formulario de buqueda ------------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const {
    makeRequest: tryGetData,
    isLoading,
    response,
  } = useRequest(searchLocations, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { dataSelect: companyDataSelect } = useExtractSelector(catalogCompanies, {
    emptyOption: true,
    label: 'label',
    value: 'value',
  });
  const { makeRequest: tryToggleStatusLocation } = useRequest(statusLocation);

  const {
    formats,
    tryDownload,
    progress: progressExport,
    isLoading: isLoadingExport,
  } = useDownloadBlob(exportFileOptions, exportFile);

  const { triggerAction } = useNativeDebounce({
    delay: 600,
  });

  const handleRequestLocations = () => {
    triggerAction().then((isOk) => {
      if (!isOk) return;
      tryGetData(form, currentPage);
    });
  };

  const handleExportFile = async (format) => {
    await tryDownload({
      type: format,
      name: 'Locaciones',
    });
  };

  // ---- Borra todos los filtros ------
  const handleClearAllFilters = () => {
    setQuerySearch('');
    setCurrentPage(undefined);
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
    // ---- limpia selects
    setStatusSelect('');
    setcompanySelect('');
  };

  // ----- Actualiza el formulario acorde cambio de filtros
  useEffect(() => {
    if (
      querySearch === undefined ||
      currentItemsPerPage === undefined ||
      datePickerValue === undefined
    )
      return;
    setForm((current) => {
      return {
        ...current,
        query: querySearch,
        paginate: currentItemsPerPage,
        initDate: datePickerValue[0] === null ? '' : datePickerValue[0],
        endDate: datePickerValue[1] === null ? '' : datePickerValue[1],
        status: statusSelect === '' ? null : statusSelect.toString(),
        company_key: companySelect === '' ? null : companySelect.toString(),
      };
    });
  }, [querySearch, currentItemsPerPage, datePickerValue, statusSelect, companySelect]);

  // ------ Actualiza la informacion de la tabla despues de la peticion -------
  useEffect(() => {
    if (!response) return;
    setDataList(response.data);
    setTotalPages(response.last_page);
    setTotalItems(response.total);
    setCurrentPage(response.current_page);
  }, [response]);

  // -------- Realiza la peticion de datos al haber un cambio en el formulario -----
  useEffect(() => {
    if (form === undefined || form.paginate === undefined) return;
    console.log('FROM | QUERY => ', form);
    handleRequestLocations();
  }, [currentPage, form]);

  // ------ Inicializacion de la vista con toda la informacion disponible -------
  useEffect(() => {
    return () => {
      setDataList([]);
    };
  }, []);

  // ------ Funciones para actualizar los filtros ------
  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) {
      return;
    }
    setQuerySearch(query);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleToggleAction = async (id, status, name) => {
    const invertStatus = !status;
    openAlert({
      title: 'Atención',
      description: `Está seguro que desea ${
        status ? 'deshabilitar' : 'habilitar'
      } a la Compañía ${name}?`,
    })
      .then((userResponse) => {
        if (!userResponse) {
          return;
        }
        tryToggleStatusLocation(id, { status: invertStatus }).then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: `Administrador ${status ? 'deshabilitado' : 'habilitado'} correctamente`,
                autoHideDuration: 3000,
              })
            );
            tryGetData(form);
          }
        });
      })
      .catch((error) => console.log(error));
  };

  return (
    <Root
      header={
        <ComposedHeader title="Locaciones" description="Analisis de datos">
          <div className="px-4 py-4">
            <ButtonWithExpand
              title={!isLoadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              startIcon={
                isLoadingExport ? (
                  <CircularProgress
                    color="inherit"
                    size="1.8rem"
                    variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
                    value={progressExport}
                  />
                ) : (
                  <SimCardDownloadOutlinedIcon />
                )
              }
            >
              {formats.map((option, index) => (
                <ButtonWithExpand.Item
                  disabled={isLoadingExport}
                  key={`${index}_${option.value}_doc`}
                  text={`Documento ${option.label}`}
                  onClick={() => handleExportFile(option.value)}
                />
              ))}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="ml-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={() => handleClearAllFilters()}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{
                            start: 'Fecha inicial',
                            end: 'Fecha final',
                          }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="px-4">
                          <BasicSelect
                            data={statusRegistryData}
                            onChange={(data) => setStatusSelect(data)}
                            title="Estatus"
                            value={statusSelect}
                            size="small"
                          />
                        </div>
                        <div className="px-4">
                          <BasicSelect
                            data={companyDataSelect}
                            onChange={(data) => setcompanySelect(data)}
                            title="Compañía"
                            value={companySelect}
                            size="small"
                          />
                        </div>
                        {/*
                          Agrega mas selects
                        */}
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoading}
                      className="w-full"
                      headers={tableRowScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column content={({ key }) => key} />
                      <ComposedTable.Column content={({ name }) => name} />
                      <ComposedTable.Column content={(item) => item.company_key} />
                      <ComposedTable.Column
                        content={({ countUsers }) => countUsers?.toString() || '0'}
                      />

                      <ComposedTable.Column
                        content={({ registriesNames }) => (
                          <PopList
                            chip
                            label={registriesNames !== undefined ? registriesNames.length : 'N/A'}
                          >
                            {registriesNames}
                          </PopList>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ torniquetsNames }) => (
                          <PopList
                            chip
                            label={torniquetsNames !== undefined ? torniquetsNames.length : 'N/A'}
                          >
                            {torniquetsNames}
                          </PopList>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ recognitionsNames }) => (
                          <PopList
                            chip
                            label={
                              recognitionsNames !== undefined ? recognitionsNames.length : 'N/A'
                            }
                          >
                            {recognitionsNames}
                          </PopList>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ kiosksNames }) => (
                          <PopList
                            chip
                            label={kiosksNames !== undefined ? kiosksNames.length : 'N/A'}
                          >
                            {kiosksNames}
                          </PopList>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ status, id }) =>
                          id ? (
                            <Switch
                              checked={status}
                              inputProps={{
                                'aria-label': 'Estatus',
                              }}
                              sx={customSwitchStyles(theme)}
                              onChange={() => handleToggleAction(id, status)}
                            />
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column content={(item) => formatDate(item.created_at)} />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default Locations;

